import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { FileBarChart, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const REPORTS = [
    "animal",
    "financial",
    "production",
    "health",
    "inventory",
    "mortality",
    "breeding",
    "vaccination",
    "feed",
    "sales",
    "expenses",
    "litter_performance",
    "survival_rate",
    "weaning_report",
    "growth_report",
    "vaccination_report",
    "treatment_report",
    "compliance_report",
    "overdue_report",
    "stock_report",
    "usage_report",
    "expiry_report",
    "purchase_report",
    "inventory_valuation_report",
    "cash_flow_report",
    "profit_loss_report",
    "balance_sheet_report",
    "accounts_receivable_report",
    "accounts_payable_report",
    "supplier_payments_report",
    "customer_payments_report",
    "payroll_report",
    "financial_summary_report",
    "daily_sales",
    "monthly_sales",
    "customer_balances",
    "product_sales",
    "animal_sales",
    "refunds",
    "payment_methods",
];

const FINANCIAL_REPORTS = new Set([
    "financial", "expenses", "sales", "cash_flow_report", "profit_loss_report",
    "balance_sheet_report", "accounts_receivable_report", "accounts_payable_report",
    "supplier_payments_report", "customer_payments_report", "payroll_report",
    "financial_summary_report", "daily_sales", "monthly_sales", "customer_balances",
    "product_sales", "animal_sales", "refunds", "payment_methods",
]);

export default function ReportsPage() {
    const { can } = useAuth();
    const availableReports = can("manage_finance") ? REPORTS : REPORTS.filter((report) => !FINANCIAL_REPORTS.has(report));
    const [type, setType] = useState("animal");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const run = async () => {
        setLoading(true);
        setError("");
        try {
            const p = new URLSearchParams();
            if (from) p.set("from_date", from);
            if (to) p.set("to_date", to);
            const r = await api.get(`/reports/${type}?${p}`);
            setRows(r.data.rows || []);
        } catch (e) {
            const message = e.response?.status === 403 ? "You are not authorized to run this report." : "The report could not be loaded. Please try again.";
            setRows([]);
            setError(message);
            toast.error(message);
        }
        setLoading(false);
    };

    const exportCsv = () => {
        if (!rows.length) return toast.error("Nothing to export");
        const keys = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach(k => s.add(k)); return s; }, new Set()));
        const csv = [keys.join(","), ...rows.map(r => keys.map(k => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${type}-report.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const cols = rows.length ? Object.keys(rows[0]).slice(0, 8) : [];

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div className="flex items-center gap-3">
                <FileBarChart className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch3.25bc96295797")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.88bc3fe3dadd")}</h1>
                </div>
            </div>

            <Card className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                        <Label className="tiny-label">{i18n.t("batch3.a666b9243f26")}</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="h-12 mt-2" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{availableReports.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch3.3f66052a107e")}</Label>
                        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-12 mt-2" data-testid="report-from-input"/>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch3.ae79ea1e9c63")}</Label>
                        <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-12 mt-2" data-testid="report-to-input"/>
                    </div>
                    <div className="flex items-end gap-2">
                        <Button onClick={run} disabled={loading} className="h-12 flex-1" data-testid="run-report-btn">{loading ? "Running..." : "Run"}</Button>
                        <Button onClick={exportCsv} variant="outline" className="h-12" data-testid="export-csv-btn"><Download className="w-4 h-4" /></Button>
                    </div>
                </div>
            </Card>

            {error ? <Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="reports-error-state">{error}</Card> : null}

            <Card className="p-4">
                <div className="text-sm text-muted-foreground mb-3">{rows.length} {i18n.t("batch3.6c30d2615392")}</div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow>{cols.map(c => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
                        <TableBody>
                            {rows.slice(0, 100).map((r, i) => (
                                <TableRow key={i}>
                                    {cols.map(c => <TableCell key={c} className="max-w-xs truncate font-mono text-xs">{String(r[c] ?? "")}</TableCell>)}
                                </TableRow>
                            ))}
                            {rows.length === 0 && <TableRow><TableCell colSpan={cols.length || 1} className="text-center py-12 text-muted-foreground">{i18n.t("batch3.768345f2ef09")}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
}
